<script setup>
  import { computed, ref } from "vue";
  import { onClickOutside } from "@vueuse/core";
  import { storeToRefs } from "pinia";
  import { Avatar, AvatarFallback } from "@/components/ui/avatar";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Check, CircleCheck, TriangleAlert } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import CommentComposer from "./CommentComposer.vue";
  import MentionText from "./MentionText.vue";
  import ReviewComment from "./ReviewComment.vue";
  import { relativeTimeFrom, shortDateTimeFrom } from "@utils/time";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";

  const props = defineProps({ thread: { type: Object, required: true } });

  const RESOLUTIONS = { applied: "Fix applied", dismissed: "Dismissed", resolved: "Resolved", fixed: "Fixed" };

  const store = useReviewCommentsStore();
  const { hasUnsavedChanges } = storeToRefs(useSurveyCardsStore());
  const { canWriteSurvey, isDraft } = storeToRefs(useSurveyStore());

  const active = ref(false);
  const article = ref(null);
  onClickOutside(article, () => (active.value = false));
  const expanded = ref(false);

  const comments = computed(() => props.thread.review_comments);
  const root = computed(() => comments.value[0]);
  const replies = computed(() => comments.value.slice(1));
  const isAi = computed(() => !!root.value?.posted_via);
  const isCheck = computed(() => !!props.thread.lint_code);
  const resolved = computed(() => props.thread.status === "resolved");
  const applied = computed(() => props.thread.resolution === "applied");
  const applicable = computed(() => store.applicable(props.thread));
  const canApply = computed(() => canWriteSurvey.value && isDraft.value);
  const applying = computed(() => store.applyingId === props.thread.id);
  const alsoTouched = computed(() => store.alsoTouchedCards(props.thread));
  const authorName = computed(() => (isCheck.value ? "Auto check" : root.value?.user.full_name));

  const reply = async (body, mentions) => {
    await store.addComment(props.thread, body, mentions);
    active.value = false;
  };
</script>

<template>
  <article
    v-if="resolved && !expanded"
    class="group flex cursor-pointer flex-col gap-1 rounded-xl bg-muted px-3 py-2.5 hover:bg-secondary"
    data-cy="review-thread"
    @click="expanded = true">
    <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Check class="size-3.5" />
      <span class="truncate">
        {{ RESOLUTIONS[thread.resolution] ?? "Resolved" }}
        <template v-if="thread.resolved_by">by {{ thread.resolved_by.full_name }}</template>
        · {{ relativeTimeFrom(thread.resolved_at ?? thread.created_at) }}
      </span>
      <Button
        variant="outline"
        size="sm"
        class="ml-auto h-6 bg-card px-2 text-xs opacity-0 group-hover:opacity-100"
        @click.stop="store.reopenThread(thread)">
        Reopen
      </Button>
    </div>
    <p class="truncate text-13 text-foreground"><MentionText :body="root?.body ?? ''" /></p>
  </article>

  <article
    v-else
    ref="article"
    class="group flex flex-col gap-2.5 rounded-xl p-3 transition-colors"
    :class="active ? 'border bg-card shadow-sm' : 'cursor-pointer bg-muted hover:bg-secondary'"
    data-cy="review-thread"
    @click="active = true">
    <div
      v-if="root"
      class="flex items-start gap-2">
      <Avatar class="size-[26px] shrink-0">
        <AvatarFallback
          :class="
            isAi || isCheck ? 'bg-primary text-primary-foreground' : 'bg-secondary text-3xs text-foreground-secondary'
          ">
          <AiStar
            v-if="isAi"
            class="size-3.5" />
          <CircleCheck
            v-else-if="isCheck"
            class="size-3.5" />
          <template v-else>{{ root.user.initials }}</template>
        </AvatarFallback>
      </Avatar>
      <div class="min-w-0 flex-1 leading-tight">
        <div class="truncate text-13 font-semibold">{{ authorName }}</div>
        <div class="text-xs text-muted-foreground">
          <template v-if="isAi">via {{ root.posted_via }} · </template>{{ relativeTimeFrom(root.created_at) }}
          <Tooltip
            v-if="root.edited_at"
            :content="`Edited ${shortDateTimeFrom(root.edited_at)}`">
            <span>· edited</span>
          </Tooltip>
        </div>
      </div>
      <div
        class="flex shrink-0 items-center gap-0.5"
        :class="active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
        @click.stop>
        <Tooltip
          v-if="!resolved && (!applicable || canWriteSurvey)"
          :content="applicable ? 'Resolve without applying the fix' : 'Resolve'">
          <Button
            variant="ghost"
            size="icon"
            class="size-7 text-muted-foreground hover:text-primary"
            aria-label="Resolve"
            @click="store.resolveThread(thread)">
            <Check class="size-4" />
          </Button>
        </Tooltip>
        <Button
          v-else-if="resolved"
          variant="outline"
          size="sm"
          class="h-7 px-2 text-xs"
          @click="store.reopenThread(thread)">
          Reopen
        </Button>
      </div>
    </div>

    <ReviewComment
      v-if="root"
      :thread="thread"
      :comment="root" />

    <div
      v-if="thread.suggestion"
      class="flex flex-col gap-2 rounded-lg border p-3"
      :class="active ? 'bg-muted' : 'bg-card'"
      data-cy="review-suggested-fix"
      @click.stop>
      <div class="flex items-center gap-1.5 text-xs font-semibold">
        <AiStar class="size-3.5 text-primary" />
        Suggested fix
      </div>
      <ul class="list-disc space-y-0.5 pl-4 text-13">
        <li
          v-for="(line, index) in thread.suggestion.summary"
          :key="index">
          {{ line }}
        </li>
      </ul>
      <p
        v-if="alsoTouched.length"
        class="text-xs text-muted-foreground">
        Modifie aussi {{ alsoTouched.join(", ") }}.
      </p>
      <p
        v-if="thread.stale_cards.length"
        class="flex items-start gap-1.5 text-xs text-status-warning-strong">
        <TriangleAlert class="mt-px size-3.5 shrink-0" />
        {{ thread.stale_cards.join(", ") }} changed after this fix was suggested, applying it may undo your edits.
      </p>
      <div
        v-if="applied"
        class="flex items-center gap-1.5 text-xs font-semibold">
        <Check class="size-3.5" />
        Applied<template v-if="thread.resolved_by"> by {{ thread.resolved_by.full_name }}</template>
        <span class="font-normal text-muted-foreground">· {{ relativeTimeFrom(thread.resolved_at) }}</span>
      </div>
      <div
        v-else-if="applicable && canApply"
        class="flex items-center gap-1.5">
        <Tooltip
          :content="hasUnsavedChanges ? 'Save your changes in the Edit tab first' : 'Applies every change at once'">
          <span class="inline-flex">
            <Button
              size="sm"
              :variant="thread.stale_cards.length ? 'outline' : 'default'"
              class="h-8 gap-1.5 px-3 text-xs"
              :disabled="hasUnsavedChanges || applying"
              @click="store.applySuggestion(thread)">
              <Check class="size-3.5" />{{ thread.stale_cards.length ? "Apply anyway" : "Apply fix" }}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="ghost"
          size="sm"
          class="h-8 px-2.5 text-xs text-muted-foreground"
          :disabled="applying"
          @click="store.resolveThread(thread)">
          Dismiss
        </Button>
      </div>
    </div>

    <ReviewComment
      v-for="comment in replies"
      :key="comment.id"
      class="border-t pt-2.5"
      :thread="thread"
      :comment="comment"
      with-meta />

    <CommentComposer
      v-if="active && !resolved"
      mode="reply"
      autofocus
      placeholder="Reply…"
      @click.stop
      @submit="reply"
      @cancel="active = false" />
  </article>
</template>
